import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import ChannelService from '@avtechsoftware/ra-javascript-utilities/src/utilities/channels/ChannelService';
import ChannelKey from '@avtechsoftware/ra-javascript-utilities/src/classes/ChannelKey';
import {
  CELSIUS_ID, FAHRENHEIT_ID, SPEED_FEET, SPEED_METERS,
} from '@avtechsoftware/ra-javascript-utilities/src/utilities/channels/ChannelValueRender';
import getChannelValueWithUnitsFromChannelKeyObjectString
  from '../../../common/ChannelValueWithUnitsFromChannelKeyObject/getChannelValueWithUnitsFromChannelKeyObjectString';
import dashboardApi from '../../services/dashboardApi';
import './ChannelWidgetView.css';

export const useChannelWidgetData = (
  context,
  { isEditMode = false, widgetId = null, refreshTick = 0 } = {},
) => {
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState(null);
  const [isDefault, setIsDefault] = useState(false);

  const { channelId = null } = context || {};

  useEffect(() => {
    if (isEditMode) return undefined;
    if (widgetId == null || channelId == null) {
      setData({ channelDetails: null });
      setIsDefault(channelId == null);
      setIsLoading(false);
      return undefined;
    }

    let cancelled = false;
    setIsLoading(true);

    dashboardApi.channel
      .getReading(widgetId)
      .then((response) => {
        if (cancelled) return;
        const body = dashboardApi.unwrapBody(response) || {};
        const details = body.sensorReading || null;
        setData({ channelDetails: details });
        setIsDefault(!details);
        setIsLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setData({ channelDetails: null });
        setIsDefault(false);
        setIsLoading(false);
      });

    return () => { cancelled = true; };
  }, [widgetId, channelId, isEditMode, refreshTick]);

  return {
    isLoading, data, viewState: {}, isDefault,
  };
};

const splitValueAndUnit = (formatted) => {
  if (formatted == null) return { value: '--', unit: '', isText: false };
  const str = String(formatted).trim();
  const match = str.match(/^([+-]?[\d.,]+)(.*)$/);
  if (!match) return { value: str, unit: '', isText: true };
  return { value: match[1], unit: match[2].trim(), isText: false };
};

const ChannelWidgetView = ({
  context, data, userPreferences, sizeX, sizeY,
}) => {
  const {
    showTimestamp = false,
  } = context || {};

  const channelDetails = data && data.channelDetails;

  const view = useMemo(() => {
    if (!channelDetails) return null;
    const cko = new ChannelKey(channelDetails.channelKey);
    const channel = ChannelService.getChannelFromChannelKeyObject(cko);
    const rawValue = channelDetails.lastSampleValue;
    const formatted = rawValue == null
      ? null
      : getChannelValueWithUnitsFromChannelKeyObjectString(
        userPreferences.temperatureScale,
        cko,
        String(rawValue),
        channelDetails.customUnit,
      );
    const { value, unit, isText } = splitValueAndUnit(formatted);

    return {
      channelDisplayName: channel ? channel.displayName : cko.channelType,
      deviceName: channelDetails.deviceName || '',
      sensorName: channelDetails.sensorName || '',
      value,
      unit,
      isText,
      timestampMs: channelDetails.lastSampleTimestamp,
    };
  }, [channelDetails, userPreferences.temperatureScale]);

  if (!view) {
    return (
      <div className="channel-widget channel-widget--empty">
        <span className="text-muted">No channel selected</span>
      </div>
    );
  }

  const formattedTime = view.timestampMs
    ? moment(view.timestampMs).tz(userPreferences.timezone || 'UTC').format('MMM D, YYYY h:mm A')
    : null;

  const formattedTimeShort = view.timestampMs
    ? moment(view.timestampMs).tz(userPreferences.timezone || 'UTC').format('h:mmA')
    : null;

  const sourceParts = [view.deviceName, view.sensorName, view.channelDisplayName].filter(Boolean);
  const source = sourceParts.length > 0 ? sourceParts.join(' • ') : null;

  const sensorLabel = view.sensorName || view.channelDisplayName;

  // Layout is picked from the widget's grid size rather than a CSS container
  // query so that a 2x1 tile always uses the horizontal layout regardless of
  // the pixel width the grid happens to hand us.
  const isCompact = (sizeX || 1) <= 1 && (sizeY || 1) <= 1;
  const layoutClass = isCompact
    ? 'channel-widget--compact'
    : 'channel-widget--wide';

  // A coarse size hint drives the reading font-size in CSS so bigger tiles
  // still get a proportionally large number without relying on viewport units.
  const tileArea = (sizeX || 1) * (sizeY || 1);
  let sizeHint = 'sm';
  if (tileArea >= 12) sizeHint = 'xl';
  else if (tileArea >= 8) sizeHint = 'lg';
  else if (tileArea >= 4) sizeHint = 'md';

  const valueClass = `channel-widget__value${view.isText ? ' channel-widget__value--text' : ''}`;

  return (
    <div className={`channel-widget ${layoutClass}`} data-size-hint={sizeHint}>
      {isCompact ? (
        <div className="channel-widget__body channel-widget__body--compact">
          <div className="channel-widget__label text-truncate w-100" title={source || sensorLabel}>
            {sensorLabel}
          </div>
          <div className="channel-widget__value-row">
            <span
              className={valueClass}
              title={view.isText ? view.value : undefined}
            >
              {view.value}
            </span>
            {view.unit && <span className="channel-widget__unit">{view.unit}</span>}
          </div>
          {showTimestamp && formattedTime && (
            <div className="channel-widget__timestamp" title={formattedTime}>
              {formattedTimeShort}
            </div>
          )}
        </div>
      ) : (
        <div className="channel-widget__body channel-widget__body--wide">
          <div className="channel-widget__labels">
            {view.deviceName && (
              <div className="channel-widget__label-line channel-widget__label-line--device" title={view.deviceName}>
                {view.deviceName}
              </div>
            )}
            {view.sensorName && (
              <div className="channel-widget__label-line channel-widget__label-line--sensor" title={view.sensorName}>
                {view.sensorName}
              </div>
            )}
            {view.channelDisplayName && (
              <div className="channel-widget__label-line channel-widget__label-line--channel" title={view.channelDisplayName}>
                {view.channelDisplayName}
              </div>
            )}
          </div>
          <div className="channel-widget__value-column">
            <div className="channel-widget__value-row">
              <span
                className={valueClass}
                title={view.isText ? view.value : undefined}
              >
                {view.value}
              </span>
              {view.unit && <span className="channel-widget__unit">{view.unit}</span>}
            </div>
            {showTimestamp && formattedTime && (
              <div
                className="channel-widget__timestamp channel-widget__timestamp--wide"
                title={formattedTime}
              >
                {formattedTime}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

ChannelWidgetView.propTypes = {
  context: PropTypes.shape({
    channelId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    showTimestamp: PropTypes.bool,
  }),
  data: PropTypes.shape({
    channelDetails: PropTypes.shape({
      channelId: PropTypes.number,
      channelKey: PropTypes.string.isRequired,
      lastSampleValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      lastSampleTimestamp: PropTypes.number,
      customUnit: PropTypes.string,
      deviceName: PropTypes.string,
      sensorName: PropTypes.string,
      componentName: PropTypes.string,
    }),
  }),
  userPreferences: PropTypes.shape({
    temperatureScale: PropTypes.oneOf([CELSIUS_ID, FAHRENHEIT_ID]),
    airSpeedScale: PropTypes.oneOf([SPEED_METERS, SPEED_FEET]),
    timezone: PropTypes.string,
  }),
  sizeX: PropTypes.number,
  sizeY: PropTypes.number,
};

ChannelWidgetView.defaultProps = {
  context: {},
  data: null,
  userPreferences: {
    temperatureScale: FAHRENHEIT_ID,
    airSpeedScale: SPEED_FEET,
    timezone: 'UTC',
  },
  sizeX: 1,
  sizeY: 1,
};

export default ChannelWidgetView;
